/* eslint-disable @typescript-eslint/no-explicit-any */
import Node from './Node';
import logger from '../../log';
import EventBus, { EventBusConfig, MessageType, Message } from './EventBus';
import PeerConfig from './PeerConfig';
import isWSAuthErrorCode from './isWSAuthErrorCode';
import { NodeData, AttachmentRequest, Detach } from './data';
import { Observable, throwError, from, interval } from 'rxjs';
import { filter, flatMap, map, retryWhen } from 'rxjs/operators';
import { PeerOffer, PeerResponse } from './NodeMessages';

export interface Tick {
  /**
   * Simple method for notifying use.
   */
  tick(): void;
}

export interface RTCSignalOps extends Tick {
  /**
   * Provide an offer message
   *
   * @param to - destination node
   * @param from - source node
   * @param offer - sdp offer
   * @returns object with a type and payload.
   */
  provideOffer(to: Node, from: Node, offer: RTCSessionDescription): void;

  /**
   * Provide an answer message
   *
   * @param to - destination node
   * @param from - source node
   * @param answer - sdp answer
   * @returns object with a type and payload.
   */
  provideAnswer(to: Node, from: Node, answer: RTCSessionDescriptionInit): void;

  /**
   * Provide an ice candidate message.
   *
   * @param to - destination node
   * @param from - source node
   * @param candidate - ice candidate
   * @returns object with a type and payload.
   */
  sendIceCandidate(to: Node, from: Node, candidate: RTCIceCandidate): void;
}

const log = logger.getLogger(__filename);

// Default timeout is 5 minutes.
const IDLE_TIME_MS_DEFAULT = 5 * 60 * 1000;

export enum STATE {
  CONNECTING = 0,
  CLOSED = 1,
  OPEN = 2,
  RECONNECTED = 3,
  ERROR = 4,
  IDLE = 5
}

export const ConnectionState = {
  PENDING: 'PENDING',
  ESTABLISHED: 'ESTABLISHED',
  FAILED: 'FAILED',
  CLOSED: 'CLOSED'
};

/// RPC Related
export enum RpcType {
  PEER_OFFER = 'PEER_OFFER',
  PEER_ANSWER = 'PEER_ANSWER',
  DISCOVER_PEERS = 'DISCOVER_PEERS',
  NODE_DISCONNECT = 'NODE_DISCONNECT',
  ICE_CANDIDATE = 'ICE_CANDIDATE',
  CONTENT_STATUS = 'CONTENT_STATUS',
  PEER_CONNECTION = 'PEER_CONNECTION',
  COORDINATOR_REQUEST = 'COORDINATOR_REQUEST',
  COORDINATOR_NOTIFY = 'COORDINATOR_NOTIFY',
  COORDINATOR_DETACH = 'COORDINATOR_DETACH',
  PING_ACK = 'PING_ACK'
}

export enum PingAck {
  PING = 'PING',
  ACK = 'ACK'
}

/**
 * Simple queue like interface for message caching.
 */
export class Queue<T> {
  arr: T[];
  constructor() {
    this.arr = [];
  }

  /**
   * Push data onto the queue.
   */
  push(data: T): void {
    this.arr.unshift(data);
  }

  /**
   * Pop off all of the data. Consider this as a copy the data into an array and reset the internal array.
   */
  popAll(): T[] {
    const copy = [...this.arr];
    this.arr = [];
    return copy.reverse();
  }
}

type Payload = {}; //eslint-disable-line

interface RpcMessage {
  type: RpcType;
  payload: Payload;
}

export interface SignalBusConfig extends EventBusConfig {
  idleShutdownMs?: number;
}

interface Handlers {
  onclose?: (event: CloseEvent) => void;
  onerror?: (error: Error) => void;
  onopen?: (event: Event) => void;
  onreconnect?: () => void;
}

export function shouldRetryConnection(
  getState: () => STATE
): (error: CloseEvent | Event | Error) => boolean {
  return (error: CloseEvent | Event | Error): boolean => {
    const state = getState();
    if (state === STATE.IDLE) {
      return false;
    }
    if ((error as CloseEvent).code) {
      return !isWSAuthErrorCode(error as CloseEvent);
    }
    return true;
  };
}

export class SignalBus implements RTCSignalOps {
  state: STATE;
  dispatchQueue: Queue<RpcMessage>;
  eb: EventBus<RpcMessage> | undefined;

  // init opts
  peerConfig: PeerConfig | undefined;
  config: SignalBusConfig | undefined;
  endpoint: string;
  coordinatorDetachUrl: string;
  rpcEventBusAddress: string;
  token: string;
  handlers: Handlers;

  // idle timer related
  idleTimeMs: number;
  lastUsed: number | undefined;

  // Configure and Establish a Connection.
  constructor() {
    this.state = STATE.CLOSED;
    // This queue is responsible for caching dispatch messages when the socket is CLOSED|ERROR
    this.dispatchQueue = new Queue();
    this.rpcEventBusAddress = 'rtc.sigserver';
    this.endpoint = 'wss://signal.kollective.app/api/rtc/eb/websocket';
    this.coordinatorDetachUrl =
      'https://signal.kollective.app/api/rtc/coordinator/detach';
    this.token = '';
    this.idleTimeMs = IDLE_TIME_MS_DEFAULT;
    this.handlers = {};
  }

  /**
   * Initialize the SignalBus
   */
  init(peerConfig: PeerConfig, handlers: Handlers): Promise<void> {
    this.peerConfig = peerConfig;
    if (this.state == STATE.CONNECTING) {
      // when we are already CONNECTING, wait till it's OPEN ore return failed.
      // This is mostly relevant to _restart calls as they can race and lead to multiple sockets.
      return interval(200)
        .pipe(
          map(() => {
            if (this.state !== STATE.OPEN) {
              throw this.state;
            }
          }),
          retryWhen(errors =>
            errors.pipe(filter(state => state === STATE.CONNECTING))
          )
        )
        .toPromise();
    }
    this.state = STATE.CONNECTING;
    this.config = peerConfig.signalingConfig;
    if (!this.config.maxReconnectAttempts) {
      // the default reconnect attempts is infinity. This leads to errors.
      this.config.maxReconnectAttempts = 8;
    }
    this.config.shouldRetry = shouldRetryConnection(() => this.state);
    this.endpoint = peerConfig.eventBusEndpointURL;
    this.rpcEventBusAddress = peerConfig.eventBusServerAddress;
    this.coordinatorDetachUrl = peerConfig.coordinatorDetachUrl;
    this.token = peerConfig.token;
    this.tick();
    this.handlers = handlers;

    // The amount of time (in milliseconds) the socket can be idle before closing down.
    if (this.config.idleShutdownMs) {
      this.idleTimeMs = this.config.idleShutdownMs;
    }
    // Set a inteval timer to check
    const idleTimer = setInterval(() => {
      // if the lastUsed var is not set ignore. However, it shouldn't be null.
      if (this.lastUsed) {
        // get the difference between lastUsed and now
        const diff = Date.now() - this.lastUsed;
        // If the diff is greater than the idle time in ms
        if (diff > this.idleTimeMs) {
          log.info('Socket is idle. Closing.');
          this.close();
          this.state = STATE.IDLE;
          clearTimeout(idleTimer);
        }
      } else {
        log.info(`lastUsed was unset - ${this.lastUsed}`, this);
        this.lastUsed = Date.now();
      }
    }, 30000);

    return new Promise((resolve, reject) => {
      this.eb = new EventBus(this.endpoint, this.config || {}, {
        onopen: (event): void => {
          log.debug('opening', event, 'handler:', !!this.handlers.onopen);
          this.state = STATE.OPEN;
          if (this.handlers.onopen) {
            this.handlers.onopen(event);
          }
          resolve();
        },
        onclose: (event): void => {
          log.debug(`closing: ${this.state}`);
          if (this.state === STATE.CLOSED || this.state === STATE.IDLE) {
            // in the case of idle, don't update because we want to stay idle for restarts.
            // don't do onClosed when we already are closed.
            return;
          }
          this.state = STATE.CLOSED;
          if (this.handlers.onclose) {
            this.handlers.onclose(event);
          }
        },
        onerror: (err): void => {
          this.state = STATE.ERROR;
          log.error(err);
          if (this.handlers.onerror) {
            this.handlers.onerror(err);
          }
          reject();
        },
        onreconnect: (event): void => {
          log.debug('Reconnecting', event);
          this.state = STATE.RECONNECTED;
          if (this.handlers.onreconnect) {
            this.handlers.onreconnect();
          }
          // dispatch all pending messages.
          // we want to copy in case reconnect fails intermittently.
          const copy = this.dispatchQueue.popAll();
          // resend all the messages. The copy does not contain messages that may get repushed in the dispatch call.
          copy.forEach(msg => this.dispatch(msg));
        }
      });
    });
  }

  /**
   * Increment internal value to track usage.
   */
  tick(): number {
    this.lastUsed = Date.now();
    return this.lastUsed;
  }

  /**
   * Close the connection and cleanup the resources.
   */
  close(): void {
    this.state = STATE.CLOSED;
    if (this.eb) {
      this.eb.close();
      this.eb = undefined;
    }
  }

  /**
   * Send a message with resulting promise containing the response.
   */
  send(message: RpcMessage): Promise<Message<any>> {
    const address = this.rpcEventBusAddress;
    if (!this.eb) {
      return Promise.reject('EventBus is undefined');
    }
    this.tick();

    if (this.state === STATE.OPEN || this.state === STATE.RECONNECTED) {
      return this.eb.request(address, message).toPromise();
    }
    if (this.state === STATE.IDLE) {
      return this._restart().then(() => {
        return this.eb
          ? this.eb.request(address, message).toPromise()
          : Promise.reject('EventBus is undefined');
      });
    }
    // we don't cache sends, as they require a response. So failing is the right idea here.
    return Promise.reject(`Failed to send. state: ${this.state}`);
  }

  /**
   * Send a message with no response.
   */
  dispatch(message: RpcMessage): void {
    const address = this.rpcEventBusAddress;
    this.tick();
    if (this.state === STATE.OPEN || this.state === STATE.RECONNECTED) {
      if (this.eb) {
        this.eb.send(address, message);
      }
    } else if (this.state === STATE.IDLE) {
      this.dispatchQueue.push(message);
      this._restart().then(() => {
        // dispatch all pending messages.
        const copy = this.dispatchQueue.popAll();
        // resend all the messages. The copy does not contain messages that may get repushed in the dispatch call.
        copy.forEach(msg => {
          this.dispatch(msg);
        });
      });
    } else {
      // add pending dispatch-able messages to send on reconnect
      this.dispatchQueue.push(message);
    }
  }

  /**
   * Send a message with resulting promise containing the response.
   */
  sendStream(message: RpcMessage): Observable<Message<any>> {
    const address = this.rpcEventBusAddress;
    if (!this.eb) {
      return throwError('EventBus is undefined');
    }
    this.tick();

    if (this.state === STATE.OPEN || this.state === STATE.RECONNECTED) {
      return this.eb.requestStream(address, message);
    }
    if (this.state === STATE.IDLE) {
      return from(this._restart()).pipe(
        flatMap(() => {
          return this.eb
            ? this.eb.requestStream(address, message)
            : throwError('EventBus is undefined');
        })
      );
    }
    // we don't cache sends, as they require a response. So failing is the right idea here.
    return throwError(`Failed to send. state: ${this.state}`);
  }

  /**
   * This implies that init has been called once before and therefore the relevant values already exist.
   */
  _restart(): Promise<void> {
    log.info('Restarting the Signalling connection.');
    if (this.peerConfig) {
      return this.init(this.peerConfig, this.handlers);
    } else {
      // really shouldn't happen as peer config will get set for first init.
      return Promise.reject(new Error('PeerConfig cannot be null for restart'));
    }
  }

  /**
   * Register a single handler
   *
   * @param type - type or address of the handler
   * @param handler - handler for type
   */
  registerHandler(
    type: string,
    handler: (error: any, message?: any) => void
  ): void {
    log.debug(`registering: ${type}`);
    if (this.eb) {
      const subscription = this.eb.listen(type).subscribe(
        next => {
          if (next.type === MessageType.ERROR) {
            handler(next);
          } else {
            handler(null, next);
          }
        },
        err => {
          log.warn('Shutting down handler subscription', err);
          subscription.unsubscribe();
        },
        () => {
          // unsubscribe when complete (graceful exit)
          subscription.unsubscribe();
        }
      );
    }
  }

  /**
   * Provide an offer message
   *
   * @param to - destination node
   * @param from - source node
   * @param offer - sdp offer
   * @returns object with a type and payload.
   */
  provideOffer(to: Node, from: Node, offer: RTCSessionDescription): void {
    this.dispatch({
      type: RpcType.PEER_OFFER,
      payload: {
        from,
        to,
        offer
      }
    });
  }

  /**
   * Provide an answer message
   *
   * @param to - destination node
   * @param from - source node
   * @param answer - sdp answer
   * @returns object with a type and payload.
   */
  provideAnswer(to: Node, from: Node, answer: RTCSessionDescriptionInit): void {
    this.dispatch({
      type: RpcType.PEER_ANSWER,
      payload: {
        from,
        to,
        answer
      }
    });
  }

  /**
   * Provide an ice candidate message.
   *
   * @param to - destination node
   * @param from - source node
   * @param candidate - ice candidate
   * @returns object with a type and payload.
   */
  sendIceCandidate(to: Node, from: Node, candidate: RTCIceCandidate): void {
    this.dispatch({
      type: RpcType.ICE_CANDIDATE,
      payload: {
        to,
        from,
        candidate
      }
    });
  }

  /**
   * Send an PING message
   *
   * @param to - destination node
   * @param from - source node
   * @param offer - PeerOffer for the other node
   */
  sendPing(to: Node, from: Node, offer: PeerOffer): void {
    this.dispatch({
      type: RpcType.PING_ACK,
      payload: {
        from,
        to,
        pingAck: PingAck.PING,
        data: offer
      }
    });
  }

  /**
   * Send an ACK message
   *
   * @param to - destination node
   * @param from - source node
   * @param response - PeerResponse payload
   */
  sendAck(to: Node, from: Node, response: PeerResponse): void {
    this.dispatch({
      type: RpcType.PING_ACK,
      payload: {
        from,
        to,
        pingAck: PingAck.ACK,
        data: response
      }
    });
  }

  request(attachmentRequest: AttachmentRequest): Observable<Message<any>> {
    return this.sendStream({
      type: RpcType.COORDINATOR_REQUEST,
      payload: attachmentRequest
    });
  }

  notify(node: NodeData): Promise<Message<any>> {
    try {
      return this.send({
        type: RpcType.COORDINATOR_NOTIFY,
        payload: node
      });
    } catch (e) {
      return Promise.reject({ error: e });
    }
  }

  detach(detach: Detach): Promise<Message<any>> {
    log.debug('Sending detach request');
    try {
      const body = { token: this.token, detach };
      if (
        navigator.sendBeacon(this.coordinatorDetachUrl, JSON.stringify(body))
      ) {
        return Promise.resolve({
          type: MessageType.RECEIVE,
          body: {}
        });
      } else {
        return Promise.reject(new Error('Failed to send Detach'));
      }
    } catch (e) {
      return Promise.reject({ error: e });
    }
  }

  sendPeerConnectionEvent(
    node: Node,
    peer: Node,
    state: unknown,
    rtcData: unknown
  ): void {
    const data: {
      nodes: Node[];
      connectionState: any;
      rtcData?: any;
    } = {
      nodes: [node, peer],
      connectionState: state
    };
    if (rtcData) {
      data.rtcData = rtcData;
    }
    return this.dispatch({
      type: RpcType.PEER_CONNECTION,
      payload: data
    });
  }
}

const SignalConnection = new SignalBus();
export default SignalConnection;
